import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  tap,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CommonSearchBarComponent } from '../../features-modules/shared/common-search-bar/common-search-bar.component';
import { CommonServiceService } from '../../utilities/services/common-service/common-service.service';
import {
  GLOBAL_SEARCH_DEBOUNCE_MS,
  GlobalSearchResult,
  GlobalSearchService,
} from '../../utilities/services/global-search/global-search.service';
import {
  Language,
  LanguageService,
} from '../../utilities/services/language.service';
import {
  TEXT_SCALE_STEPS,
  TextScale,
  TextScaleService,
} from '../../utilities/services/text-scale/text-scale.service';
import { ThemeServiceService, ThemeType } from '../../utilities/services/theme-service.service';
import {
  UserMenuItem,
  UserMenuService,
} from '../../utilities/services/user-menu/user-menu.service';

/** One primary navigation entry in the template-1 rail. */
export interface T1NavItem {
  /** Stable key, also selects the inline SVG icon in the template. */
  readonly id: string;
  /** i18n key for the visible label (shown when the rail is expanded). */
  readonly labelKey: string;
  /** Router URL, identical to the template-0 header's target for this tab. */
  readonly route: string;
  /** URL prefix used to mark the item active. */
  readonly match: string;
  /** Tenant feature flags may hide an entry (mirrors the header's gating). */
  readonly visible: boolean;
  /** Live pulse dot, shown on the "Live" entry only. */
  readonly hasLiveDot?: boolean;
}

/** Which anchored popup the rail is currently showing. */
type RailPopup = 'language' | 'profile' | 'textsize' | 'theme' | null;

/** One entry in the named-theme picker, mirrors the template-0 header's own list. */
export interface T1ThemeOption {
  readonly value: ThemeType;
  readonly labelKey: string;
}

/**
 * Tenant skins offered in the rail — same seven values `ThemeServiceService`
 * validates against, so a new entry only needs adding here and there.
 */
const THEME_OPTIONS: readonly T1ThemeOption[] = [
  { value: 'default-custom-theme', labelKey: 'template1_theme_default' },
  { value: 'switz-custom-theme', labelKey: 'template1_theme_switz' },
  { value: 'rfetm-custom-theme', labelKey: 'template1_theme_rfetm' },
  { value: 'nbtf-custom-theme', labelKey: 'template1_theme_nbtf' },
  { value: 'ptta-custom-theme', labelKey: 'template1_theme_ptta' },
  { value: 'ttnz-custom-theme', labelKey: 'template1_theme_ntzs' },
  { value: 'sbtf-custom-theme', labelKey: 'template1_theme_sbtf' },
];

/**
 * Nav slots in the mobile bottom bar before the profile button.
 *
 * The rail lists up to nine tenant-gated entries; a phone bar fits five
 * targets at 44px. Entries past this cut are not dropped — they are listed at
 * the top of the profile sheet (`mobileOverflowNav`), so nothing reachable on
 * desktop becomes unreachable on a phone.
 */
const MOBILE_NAV_SLOTS = 4;

/** One entry in the reader text-size picker. */
export interface T1TextScaleOption {
  /** Size applied when the row is chosen. */
  readonly scale: TextScale;
  /** i18n key for the row's label. */
  readonly labelKey: string;
  /** Modifier class that renders the row's own label at that size. */
  readonly previewClass: string;
}

/**
 * Reader sizes offered in the rail, labelled smallest to largest.
 *
 * Declared here rather than in `TextScaleService` so the service stays free of
 * i18n concerns — it owns the values, the rail owns how they are named.
 */
const TEXT_SCALE_OPTIONS: readonly T1TextScaleOption[] = [
  {
    scale: TEXT_SCALE_STEPS[0],
    labelKey: 'text_size_small',
    previewClass: 't1-rail__text-size--sm',
  },
  {
    scale: TEXT_SCALE_STEPS[1],
    labelKey: 'text_size_default',
    previewClass: 't1-rail__text-size--md',
  },
  {
    scale: TEXT_SCALE_STEPS[2],
    labelKey: 'text_size_large',
    previewClass: 't1-rail__text-size--lg',
  },
  {
    scale: TEXT_SCALE_STEPS[3],
    labelKey: 'text_size_extra_large',
    previewClass: 't1-rail__text-size--xl',
  },
];

/**
 * Template-1 (OTT layout) primary navigation — a floating glass rail that
 * expands on hover, replacing the template-0 top header.
 *
 * Presentation only: navigation targets, search, the profile menu, the language
 * list and theme toggling are all delegated to the same services the template-0
 * header uses, so no business logic is duplicated between the two layouts.
 */
@Component({
  selector: 'stupa-template1-sidebar',
  standalone: true,
  imports: [NgTemplateOutlet, TranslateModule, CommonSearchBarComponent],
  templateUrl: './template1-sidebar.component.html',
  styleUrl: './template1-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Template1SidebarComponent implements OnInit {
  /**
   * Mirrors the template-0 header's output so `AppComponent.changeLanguage()`
   * stays the single place that persists the active language.
   */
  @Output() readonly languageChange = new EventEmitter<string>();

  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeServiceService);
  private readonly textScaleService = inject(TextScaleService);
  private readonly languageService = inject(LanguageService);
  private readonly translateService = inject(TranslateService);
  private readonly searchService = inject(GlobalSearchService);
  private readonly userMenu = inject(UserMenuService);
  private readonly commonService = inject(CommonServiceService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  /** Which anchored popup is visible, if any. */
  openPopup: RailPopup = null;

  /** Rebuilt whenever login state changes — some entries are session-gated. */
  navItems: readonly T1NavItem[] = [];

  /**
   * The `navItems` slice shown in the mobile bottom bar.
   *
   * Sliced once per session change alongside `navItems` rather than exposed as
   * a getter: a getter would allocate a new array on every change-detection
   * pass and defeat `@for` tracking, the same reason `profileMenu` is cached.
   */
  mobilePrimaryNav: readonly T1NavItem[] = [];

  /** Entries the bottom bar had no room for; listed in the profile sheet. */
  mobileOverflowNav: readonly T1NavItem[] = [];

  /**
   * Whether the mobile search sheet is open.
   *
   * A phone has no room for a persistent search field beside the logo, so the
   * top bar shows a button that opens the same input in a sheet. The query and
   * results are the rail's own state — only the presentation differs.
   */
  isMobileSearchOpen = false;

  /**
   * Profile rows, cached rather than rebuilt per change-detection pass.
   *
   * `buildMenu()` allocates a new array on every call, so binding it through a
   * getter would hand `@for` a fresh identity each cycle and defeat tracking.
   */
  profileMenu: readonly UserMenuItem[] = [];

  /** Cached session flag — avoids a `localStorage` read on every CD pass. */
  isLoggedIn = false;

  /** Shortened to "Profile" when the rail already lists the "My Profile" dashboard. */
  profileLabelKey = 'my_profile';

  /** Id of the rail entry matching the current route, or `null`. */
  activeNavId: string | null = null;

  readonly languages: readonly Language[] =
    this.languageService.getAvailableLanguages();

  /**
   * Tenant logo for the rail's orb.
   *
   * Always the *light* variant: the orb background is the brand accent in both
   * themes, so the light logo is the contrast-correct one either way.
   */
  readonly tenantLogo = environment.logo_light || environment.logo;
  readonly tenantTitle = environment.title;

  /**
   * Two-letter mark for the logo orb when no tenant logo image is configured.
   * Derived from build-time config, so computed once rather than per pass.
   */
  readonly logoInitials = Template1SidebarComponent.toInitials(
    environment.title,
  );

  // ── Search state ──
  searchQuery = '';
  searchResults: readonly GlobalSearchResult[] = [];
  isSearchLoading = false;
  isSearchFocused = false;
  private readonly searchInput$ = new Subject<string>();

  ngOnInit(): void {
    this.refreshSession();
    this.syncActiveEntry(this.router.url);

    // Session-gated entries (dashboard, profile menu) must react to sign
    // in/out without a page reload — same subject the header listens to.
    this.commonService.is_login_success
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshSession();
        this.syncActiveEntry(this.router.url);
        this.cdr.markForCheck();
      });

    // Keep the active highlight in sync with router navigation (OnPush needs
    // an explicit change-detection nudge here).
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        this.syncActiveEntry(event.urlAfterRedirects);
        this.closePopups();
        this.cdr.markForCheck();
      });

    // Search stream: wait for typing to settle, drop repeats, and cancel any
    // in-flight request when a newer query arrives.
    this.searchInput$
      .pipe(
        debounceTime(GLOBAL_SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        tap(() => {
          this.isSearchLoading = true;
          this.cdr.markForCheck();
        }),
        switchMap((query) =>
          this.searchService
            .search(query)
            // A failed lookup must not kill the stream — recover with no results.
            .pipe(catchError(() => of([] as GlobalSearchResult[]))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((results) => {
        this.searchResults = results;
        this.isSearchLoading = false;
        this.cdr.markForCheck();
      });
  }

  /**
   * Rail stays expanded without hover while the user is mid-interaction —
   * typing a query or reading an open popup. Hover alone is handled in CSS so
   * the expanded state can never get stuck.
   */
  get isPinned(): boolean {
    return (
      this.openPopup !== null ||
      this.isSearchFocused ||
      this.searchQuery.length > 0
    );
  }

  /** Reader sizes shown in the text-size popup. */
  readonly textScaleOptions = TEXT_SCALE_OPTIONS;

  /** Active reader size — a signal, so `OnPush` repaints the selected row. */
  readonly currentTextScale = this.textScaleService.scale;

  /**
   * Applies a reader text size and closes the picker.
   *
   * @param scale Size the user chose.
   */
  selectTextScale(scale: TextScale): void {
    this.textScaleService.set(scale);
    this.closePopups();
  }

  /** True when dark mode is on — drives the sun/moon icon swap. */
  get isDark(): boolean {
    return this.themeService.getTheme();
  }

  /** Tenant skins shown in the theme popup. */
  readonly themeOptions = THEME_OPTIONS;

  /**
   * Named tenant skin picker — a separate, orthogonal setting from dark/light
   * mode ({@link isDark}/{@link toggleTheme}): the two stack independently as
   * separate `<body>` classes via `ThemeServiceService`. Gated the same way
   * the template-0 header gates it, so both layouts expose the same picker to
   * the same tenants rather than template 1 showing it more broadly.
   */
  readonly isThemeSwitchAvailable =
    environment.tenant_Name === 'dev' ||
    environment.tenant_Name === 'staging' ||
    environment.tenant_Name === 'preprod';

  /** Currently active tenant skin, for the theme popup's selected state. */
  get currentTheme(): ThemeType {
    return this.themeService.getCurrentTheme();
  }

  /**
   * Applies a tenant skin and closes the picker.
   *
   * @param theme Skin the user chose.
   */
  selectTheme(theme: ThemeType): void {
    this.themeService.setTheme(theme);
    this.closePopups();
    this.cdr.markForCheck();
  }

  /** Currently active language code, for the popup's selected state. */
  get currentLang(): string {
    return (
      this.translateService.currentLang ||
      this.translateService.getFallbackLang() ||
      'en'
    );
  }

  /**
   * Navigates to a rail entry's route.
   *
   * @param item Entry that was activated.
   */
  navigate(item: T1NavItem): void {
    this.closePopups();
    this.clearSearch();
    void this.router.navigate([item.route]);
  }

  /** Brand logo returns to the home page on every device. */
  goHome(): void {
    this.closePopups();
    this.clearSearch();
    void this.router.navigate(['/home']);
  }

  /** Toggles dark mode via the app-wide theme service (shared with template 0). */
  toggleTheme(): void {
    this.themeService.toggleTheme();
    this.cdr.markForCheck();
  }

  /**
   * Opens/closes an anchored popup.
   *
   * @param popup Popup to toggle.
   * @param event Click event; stopped so the document handler doesn't
   *   immediately close the popup that was just opened.
   */
  togglePopup(popup: Exclude<RailPopup, null>, event: Event): void {
    event.stopPropagation();
    this.openPopup = this.openPopup === popup ? null : popup;
  }

  /**
   * Applies a language, reusing the header's exact contract: emit upward so
   * `AppComponent.changeLanguage()` performs the switch and persistence.
   *
   * @param lang Language code to activate.
   */
  selectLanguage(lang: string): void {
    this.languageChange.emit(lang);
    this.closePopups();
  }

  /**
   * Runs a profile-menu action through the shared user-menu service.
   *
   * @param item Row the user activated.
   */
  runProfileAction(item: UserMenuItem): void {
    this.closePopups();
    this.userMenu.run(item.id);
  }

  /**
   * Feeds keystrokes into the debounced search stream.
   *
   * @param event Input event from the search field.
   */
  onSearchInput(event: Event): void {
    this.feedSearchQuery((event.target as HTMLInputElement).value);
  }

  /**
   * Same feed as {@link onSearchInput}, for the mobile top-bar search — the
   * shared `<stupa-common-search-bar>` (the same component/animation the
   * Events and Live Scorecard pages use) already debounces keystrokes itself
   * and emits the plain string, so this only needs to forward it into the
   * existing global-search stream rather than re-read an `Event`.
   *
   * @param value Current query from the shared search bar.
   */
  onGlobalSearchChange(value: string): void {
    this.feedSearchQuery(value ?? '');
  }

  /** Shared debounced-search feed for both the desktop rail and mobile search inputs. */
  private feedSearchQuery(value: string): void {
    this.searchQuery = value;

    // Below the minimum length there is nothing to show — clear immediately
    // instead of waiting for a request that would not be sent.
    if (!this.searchService.isSearchable(value)) {
      this.searchResults = [];
      this.isSearchLoading = false;
      return;
    }
    this.searchInput$.next(value.trim());
  }

  /**
   * Opens/closes the mobile search sheet.
   *
   * @param event Tap on the top-bar search button; stopped so the document
   *   handler does not immediately close the sheet that was just opened.
   */
  toggleMobileSearch(event: Event): void {
    event.stopPropagation();
    this.closePopups();
    this.isMobileSearchOpen = !this.isMobileSearchOpen;
    if (!this.isMobileSearchOpen) {
      this.clearSearch();
    }
  }

  /**
   * Mirrors the shared search bar's own expand/collapse state — same
   * component/animation as the Events and Live Scorecard pages'
   * `<stupa-common-search-bar>`. Drives whether the other top-bar buttons
   * hide and the results dropdown can show.
   *
   * @param expanded Whether the shared search bar is now expanded.
   */
  onMobileSearchExpandedChange(expanded: boolean): void {
    this.closePopups();
    this.isMobileSearchOpen = expanded;
    if (!expanded) {
      this.clearSearch();
    }
  }

  /** Marks the search field focused so the rail stays expanded while typing. */
  onSearchFocus(): void {
    this.isSearchFocused = true;
  }

  /** Focus left the search field; results stay until explicitly cleared. */
  onSearchBlur(): void {
    this.isSearchFocused = false;
  }

  /**
   * Opens a search hit using the shared routing rules.
   *
   * @param result Result the user activated.
   */
  openResult(result: GlobalSearchResult): void {
    this.searchService.openResult(result);
    this.clearSearch();
  }

  /**
   * Empties the query and results, and dismisses the mobile search sheet.
   *
   * Every existing caller (navigating away, opening a result, clicking out)
   * is a case where the sheet should close too, so the flag is reset here
   * rather than in a second closer the callers would have to remember.
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.searchResults = [];
    this.isSearchLoading = false;
    this.isSearchFocused = false;
    this.isMobileSearchOpen = false;
  }

  /** Closes any open popup. */
  closePopups(): void {
    this.openPopup = null;
  }

  /** Clicking anywhere outside the rail dismisses popups and search results. */
  @HostListener('document:click')
  onDocumentClick(): void {
    this.dismissOverlays();
  }

  /** Escape closes popups/results without needing a pointer. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.dismissOverlays();
  }

  /**
   * Closes whatever transient surface is open.
   *
   * The mobile search sheet is included in the guard: it can legitimately be
   * open with an empty query and no results, so testing the query alone would
   * leave a full-screen sheet that neither an outside tap nor Escape dismisses.
   */
  private dismissOverlays(): void {
    if (
      this.openPopup !== null ||
      this.searchResults.length > 0 ||
      this.isMobileSearchOpen
    ) {
      this.closePopups();
      this.clearSearch();
      this.cdr.markForCheck();
    }
  }

  /**
   * Re-reads session-dependent state.
   *
   * Called once on init and on every sign-in/out, so the template binds plain
   * fields instead of getters that would hit `localStorage` and allocate a new
   * menu array on every change-detection pass.
   */
  private refreshSession(): void {
    this.isLoggedIn = this.userMenu.isLoggedIn;
    this.profileMenu = this.userMenu.buildMenu();
    this.navItems = this.buildNavItems();
    this.profileLabelKey = this.navItems.some((item) => item.id === 'dashboard')
      ? 'profile'
      : 'my_profile';
    this.mobilePrimaryNav = this.navItems.slice(0, MOBILE_NAV_SLOTS);
    this.mobileOverflowNav = this.navItems.slice(MOBILE_NAV_SLOTS);
  }

  /**
   * Resolves which rail entry the current URL belongs to.
   *
   * Precomputed on navigation so the template compares two strings per row
   * rather than calling a matcher for every entry on every CD pass.
   *
   * @param url Active router URL.
   */
  private syncActiveEntry(url: string): void {
    this.activeNavId =
      this.navItems.find((item) => url.startsWith(item.match))?.id ?? null;
  }

  /**
   * Reduces a tenant title to a two-letter mark.
   *
   * @param title Tenant display title.
   * @returns Up to two uppercase initials, or an empty string.
   */
  private static toInitials(title: string): string {
    const words = (title || '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      return '';
    }
    const letters =
      words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0];
    return letters.toUpperCase();
  }

  /**
   * Builds the rail entries, honouring the same tenant feature flags and
   * session checks the template-0 header applies, so both layouts expose an
   * identical menu.
   *
   * @returns Ordered, visible navigation entries.
   */
  private buildNavItems(): readonly T1NavItem[] {
    const items: readonly T1NavItem[] = [
      {
        id: 'dashboard',
        labelKey: 'my_profile',
        route: '/my-profile-dashboard',
        match: '/my-profile-dashboard',
        // Same triple condition the header uses for its "My Profile" tab.
        visible:
          this.userMenu.isLoggedIn &&
          this.userMenu.isPlayer &&
          environment.show_player_dashboad === true,
      },
      {
        id: 'home',
        labelKey: 'home',
        route: '/home',
        match: '/home',
        visible: true,
      },
      {
        id: 'events',
        labelKey: 'events',
        route: '/events/1',
        match: '/events',
        visible: true,
      },
      {
        id: 'videos',
        labelKey: 'video_collection',
        route: '/broadcasts/4',
        match: '/broadcasts',
        visible: environment.show_broadcasts === true,
      },
      {
        id: 'live',
        labelKey: 'live',
        route: '/live',
        match: '/live',
        visible: environment.show_live === true,
        hasLiveDot: true,
      },
      {
        id: 'ranking',
        labelKey: 'ranking',
        route: '/ranking',
        match: '/ranking',
        visible: environment.use_ranking === true,
      },
      {
        id: 'membership',
        labelKey: 'license',
        route: '/membership',
        match: '/membership',
        visible: environment.is_membership_available === true,
      },
      {
        id: 'sub_licenses',
        labelKey: 'sub_license',
        route: '/sub-licenses',
        match: '/sub-licenses',
        visible: environment.show_sub_license === true,
      },
      {
        id: 'rating',
        labelKey: 'rating',
        route: '/rating',
        match: '/rating',
        visible: environment.use_rating === true,
      },
    ];

    return items.filter((item) => item.visible);
  }
}
